import React from 'react';
import {
  Box,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import PageHeader from '../../../components/PageHeader';
import DataState, { TableSkeleton } from '../../../components/DataState';
import { colors } from '../../../design-system/tokens';
import SourceStatusBanner from './SourceStatusBanner';
import { useModernwashResource } from './useModernwashResource';
import { formatTime } from './format';

/**
 * ModernWash 목록 화면 공통 셸 — 차량/LPR/세차 프로그램/RF 카드가 공유한다.
 *
 * 헤더(제목·새로고침·동기화 시각) + 소스 봉투 배너 + items 표를 한 틀로 그린다.
 * 각 화면은 columns(열 정의)와 fetcher 만 다르게 넘긴다. 표 스타일은 Members/Dashboard
 * 의 운영 표와 동일하게 맞춘다(가로 스크롤, size="small", sticky 없음).
 *
 * columns: [{ key, label, align?, render:(row)=>node }]
 */
const ModernwashListScreen = ({
  title,
  description,
  fetcher,
  columns,
  getRowKey,
  emptyText = '표시할 항목이 없어요.',
  minWidth = 720,
}) => {
  const { loading, reload, source, items } = useModernwashResource(fetcher);
  const colCount = columns.length;

  return (
    <Box>
      <Stack
        direction="row"
        spacing={2}
        sx={{
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          rowGap: 1,
        }}
      >
        <PageHeader title={title} description={description} />
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexShrink: 0 }}>
          {!loading && !source.blocking && source.fetchedAt && (
            <Typography variant="caption" sx={{ color: colors.text.muted, whiteSpace: 'nowrap' }}>
              동기화 {formatTime(source.fetchedAt)}
            </Typography>
          )}
          <Tooltip title="새로고침" arrow>
            <span>
              <IconButton onClick={reload} disabled={loading} aria-label="새로고침">
                <RefreshRoundedIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Stack>

      {loading ? (
        <TableSkeleton rows={6} cols={colCount} />
      ) : (
        <>
          <SourceStatusBanner state={source} onRetry={reload} />
          {!source.blocking &&
            (items.length > 0 ? (
              <Paper sx={{ p: 0, overflow: 'hidden' }}>
                <TableContainer sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                  <Table size="small" sx={{ minWidth }}>
                    <TableHead>
                      <TableRow>
                        {columns.map((col) => (
                          <TableCell
                            key={col.key}
                            align={col.align || 'left'}
                            sx={{ whiteSpace: 'nowrap' }}
                          >
                            {col.label}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {items.map((row, index) => (
                        <TableRow key={getRowKey ? getRowKey(row, index) : index} hover>
                          {columns.map((col) => (
                            <TableCell key={col.key} align={col.align || 'left'}>
                              {col.render(row)}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            ) : (
              <DataState loading={false} empty emptyText={emptyText} />
            ))}
        </>
      )}
    </Box>
  );
};

export default ModernwashListScreen;
